import { useEffect } from 'react';

/**
 * Set document.title and a standard set of OG / Twitter meta tags for the
 * current page. Works for the browser tab and for crawlers that execute
 * JavaScript. Crawlers that do not (e.g. WhatsApp link unfurling) won't see
 * these — that requires SSR or static prerendering and is out of scope.
 *
 * Tags managed: description, og:title, og:description, og:image, og:type,
 * og:url, twitter:card, twitter:title, twitter:description, twitter:image.
 *
 * Passing null/undefined for a field removes that tag for the page lifetime.
 */
const META_TAGS = [
  { name: 'description',          source: 'description' },
  { property: 'og:title',         source: 'title' },
  { property: 'og:description',   source: 'description' },
  { property: 'og:image',         source: 'image' },
  { property: 'og:type',          source: 'type' },
  { property: 'og:url',           source: 'url' },
  { name: 'twitter:card',         source: 'card' },
  { name: 'twitter:title',        source: 'title' },
  { name: 'twitter:description',  source: 'description' },
  { name: 'twitter:image',        source: 'image' },
];

const upsertMeta = ({ name, property }, content) => {
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let el = document.head.querySelector(selector);
  if (content == null || content === '') {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    if (name) el.setAttribute('name', name);
    if (property) el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const usePageMeta = ({ title, description, image, type = 'website' } = {}) => {
  useEffect(() => {
    if (title) document.title = title;
    const values = {
      title,
      description,
      image,
      type,
      url: typeof window !== 'undefined' ? window.location.href : null,
      card: image ? 'summary_large_image' : 'summary',
    };
    META_TAGS.forEach((tag) => upsertMeta(tag, values[tag.source]));
  }, [title, description, image, type]);
};

export default usePageMeta;
